import { Combobox, Transition } from "@headlessui/react";
import classNames from "classnames";
import { LoadingIcon } from "components/LoadingIcon";
import { ButtonVariant, CoButton } from "components/CoButton/CoButton";
import { useLibraries } from "features/libraries";
import { Library } from "helpers/api";
import useDebounce from "hooks/useDebounce";
import useLabels from "hooks/useLabels";
import { Landmark } from "lucide-react";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export enum modes {
  insideModalMode = "inside-modal",
  defaultMode = "default",
}

type PropsType = {
  onChange: (item: Library | null) => void;
  value: Library | null;
  mode?: "inside-modal" | "default";
};

// Virtual scrolling constants
const ITEM_HEIGHT = 48; // Height of each library item
const VISIBLE_ITEMS = 8; // Number of items visible at once
const BUFFER_ITEMS = 15; // Extra items to render above/below for smooth scrolling

export const LIBRARY_SELECT_BOX_ID = "library-select-box";

// Optimized image component with error handling
const LibraryLogo = ({
  src,
  alt,
  className,
}: {
  src?: string;
  alt: string;
  className: string;
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    setImageError(false);
    setImageLoaded(false);
  }, [src]);

  if (!src || imageError) {
    return <Landmark size={16} className={className} />;
  }

  return (
    <img
      src={src}
      alt={alt}
      className={classNames(className, { "opacity-0": !imageLoaded })}
      loading="lazy"
      onLoad={() => setImageLoaded(true)}
      onError={() => setImageError(true)}
      style={{
        transition: "opacity 0.2s ease-in-out",
        opacity: imageLoaded ? 1 : 0,
      }}
    />
  );
};

export const LibrarySelectBox = ({
  onChange,
  value,
  mode = modes.defaultMode,
}: PropsType) => {
  const [query, setQuery] = useState("");
  const [scrollTop, setScrollTop] = useState(0);
  const [displayValue, setDisplayValue] = useState<Library | null>(value);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Pprevent excessive filtering
  const debouncedQuery = useDebounce(query, 150);

  const [labels] = useLabels("screens.accounts");

  const {
    libraries,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useLibraries(value?.library_id);

  useEffect(() => {
    setDisplayValue(value);
  }, [value]);

  const handleSelect = (item: Library) => {
    setDisplayValue(item);
    setQuery("");
    onChange && onChange(item);
    setIsOpen(false);
  };

  // Clear selection handler
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setQuery("");
    setDisplayValue(null);
    onChange && onChange(null);
  };

  // Handle input click to open dropdown
  const handleInputClick = () => {
    setIsOpen(true);
    // Ensure input remains focused for typing
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Filter libraries based on debounced query
  const filtered = useMemo(() => {
    if (debouncedQuery.trim() === "") {
      return libraries;
    }
    const queryLower = debouncedQuery.toLowerCase();
    return libraries.filter((library) => {
      return (
        library.library_name?.toLowerCase().includes(queryLower) ||
        library.primary_domain?.toLowerCase().includes(queryLower)
      );
    });
  }, [libraries, debouncedQuery]);

  const selectedItemIndex = value
    ? filtered.findIndex((item) => item.library_id === value.library_id)
    : -1;

  // Ensure selected item is always visible and at the top if it exists
  const displayFiltered = useMemo(() => {
    if (filtered.length && selectedItemIndex > 0) {
      // make selected item first to force options to open with selected item at the top
      const result = [...filtered];
      result.unshift(result.splice(selectedItemIndex, 1)[0]);
      return result;
    }
    return filtered;
  }, [filtered, selectedItemIndex]);

  // Intersection observer for lazy loading
  const observerRef = useRef<IntersectionObserver | null>(null);
  const optionsRef = useRef<HTMLUListElement>(null);

  // Reset scroll to top immediately whenever query changes
  useEffect(() => {
    if (optionsRef.current) {
      optionsRef.current.scrollTop = 0;
      setScrollTop(0);
    }
  }, [debouncedQuery]);

  // Virtual scrolling calculations
  const totalHeight = displayFiltered.length * ITEM_HEIGHT;
  const startIndex = Math.max(
    0,
    Math.floor(scrollTop / ITEM_HEIGHT) - BUFFER_ITEMS
  );
  const endIndex = Math.min(
    displayFiltered.length,
    Math.ceil((scrollTop + VISIBLE_ITEMS * ITEM_HEIGHT) / ITEM_HEIGHT) +
      BUFFER_ITEMS
  );
  const visibleItems = displayFiltered.slice(startIndex, endIndex);
  const offsetY = startIndex * ITEM_HEIGHT;

  const loadMoreCallback = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  // Set up intersection observer when the load more element is available
  const setLoadMoreRef = useCallback(
    (node: HTMLDivElement | null) => {
      // Clean up previous observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      if (node && hasNextPage) {
        observerRef.current = new IntersectionObserver(loadMoreCallback, {
          rootMargin: "50px",
          threshold: 0.1,
        });
        observerRef.current.observe(node);
      }
    },
    [hasNextPage, loadMoreCallback]
  );

  // Clean up observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(`#${LIBRARY_SELECT_BOX_ID}-wrapper`)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("touchstart", handleClickOutside);
      };
    }
  }, [isOpen]);

  // Handle scroll for virtual scrolling
  const handleScroll = useCallback((e: React.UIEvent<HTMLUListElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  if (isLoading) {
    return (
      <div className="relative border border-border-base pr-3 py-2 w-full pl-12 rounded-xl focus:outline-hidden bg-bg-base">
        <div className="absolute left-2 top-2 bottom-2 w-7 h-7 bg-bg-faint rounded-xl animate-pulse" />
        <span className="text-fg-muted">{labels["loading-institutions"]}</span>
      </div>
    );
  }

  return (
    <Combobox
      value={displayValue}
      onChange={handleSelect}
      as="div"
      id={`${LIBRARY_SELECT_BOX_ID}-wrapper`}
      className={classNames("relative", {
        "md:static": mode === modes.insideModalMode,
      })}
    >
      <div className="relative">
        <Combobox.Input
          ref={inputRef}
          className={classNames(
            `bg-bg-base border border-border-base pr-10 py-2 w-full pl-12 rounded-t-xl focus:outline-hidden cursor-pointer`,
            {
              "rounded-b-xl": !isOpen,
            }
          )}
          displayValue={(item: Library | null) =>
            query || item?.library_name || ""
          }
          onChange={(event) => {
            setQuery(event.target.value);
            setIsOpen(true);
          }}
          onClick={handleInputClick}
          onFocus={() => setIsOpen(true)}
          placeholder={labels["select-institution"]}
          id={LIBRARY_SELECT_BOX_ID}
        />
        <span className="absolute left-2 top-2 bottom-2 w-7 h-7 inline-flex justify-center items-center font-semibold text-[10px] rounded-lg bg-bg-faint pointer-events-none">
          {displayValue ? (
            <LibraryLogo
              src={displayValue.logo}
              alt={displayValue.library_name || "Library"}
              className="w-5 h-4 object-contain"
            />
          ) : (
            <Landmark className="w-5 h-4 object-contain" />
          )}
        </span>
        <div className="absolute right-2 top-2 bottom-2 flex items-center">
          {displayValue ? (
            <CoButton
              type="button"
              aria-label="Clear selection"
              onClick={handleClear}
              variant={ButtonVariant.Flat}
              size="sm"
              className="w-6 h-6 rounded-md hover:bg-bg-faint focus:outline-hidden"
              icon="icon-x text-fg-muted"
              tabIndex={0}
            />
          ) : (
            <Combobox.Button
              className="flex items-center justify-center w-6 h-6"
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? (
                <i className="icon-chevron-up text-xl" />
              ) : (
                <i className="icon-chevron-down text-xl" />
              )}
            </Combobox.Button>
          )}
        </div>
      </div>
      <Transition
        show={isOpen}
        as={Fragment}
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        afterLeave={() => setQuery("")}
      >
        <Combobox.Options
          static
          ref={optionsRef}
          className={classNames(
            "absolute max-h-80 w-full overflow-auto rounded-b-lg bg-bg-base py-1 shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm z-50 scrollbar-hide",
            { "md:max-w-[468px]": mode === modes.insideModalMode }
          )}
          onScroll={handleScroll}
        >
          {!displayFiltered.length &&
            !isLoading &&
            !hasNextPage &&
            !isFetchingNextPage && (
              <div className="py-3 px-2 text-center tiny-normal text-fg-muted">
                {labels["no-matches-found"].replace(
                  "{query}",
                  debouncedQuery.trim()
                )}
              </div>
            )}

          {/* Virtual scrolling container */}
          <div style={{ height: totalHeight, position: "relative" }}>
            <div style={{ transform: `translateY(${offsetY}px)` }}>
              {visibleItems.map((lib) => (
                <Combobox.Option key={lib.library_id} value={lib}>
                  {({ active, selected }) => (
                    <div
                      className={classNames(
                        "px-2 mx-2 py-2 flex justify-between items-center cursor-pointer rounded-lg hover:bg-bg-faint",
                        { "bg-bg-faint": active }
                      )}
                      style={{ height: ITEM_HEIGHT }}
                    >
                      <div className="flex items-center">
                        <span className="w-7 h-7 inline-flex justify-center items-center text-[10px] rounded-lg font-semibold bg-bg-faint mr-3">
                          <LibraryLogo
                            src={lib.logo}
                            alt={lib.library_name || "Library"}
                            className="w-5 h-3 object-contain"
                          />
                        </span>
                        <div>
                          <span className="tiny-md">{lib.library_name}</span>
                          {lib.primary_domain && (
                            <span className="tiny-normal text-fg-muted ml-2">
                              {lib.primary_domain}
                            </span>
                          )}
                        </div>
                      </div>
                      {selected && (
                        <span>
                          <i className="icon-check text-xl text-fg-muted" />
                        </span>
                      )}
                    </div>
                  )}
                </Combobox.Option>
              ))}
            </div>
          </div>

          {/* Load more trigger */}
          {hasNextPage && (
            <div
              ref={setLoadMoreRef}
              className="py-2 px-2 flex justify-center items-center text-center"
            >
              <LoadingIcon size={16} />
            </div>
          )}
        </Combobox.Options>
      </Transition>
    </Combobox>
  );
};
